/**
 * HorizontalStepTrack
 *
 * Compact ~48px strip of pipeline step pills connected by a progress line.
 * Shown at the top of the right panel during content generation.
 */

import { CheckCircle2 } from 'lucide-react'
import type { StepDef, PhaseEvent } from './GenerationSteps'

interface HorizontalStepTrackProps {
  steps: StepDef[]
  phaseEvents: Record<string, PhaseEvent>
  activePhase: string | null
}

export function HorizontalStepTrack({
  steps,
  phaseEvents,
  activePhase,
}: HorizontalStepTrackProps) {
  return (
    <div className="flex items-center gap-0 w-full overflow-x-auto py-3 px-1 select-none">
      {steps.map((step, index) => {
        const isDone = step.phaseKey in phaseEvents
        const isActive = step.phaseKey === activePhase && !isDone

        return (
          <div key={step.id} className="flex items-center min-w-0 shrink-0">
            {/* Pill */}
            <div className="relative group flex flex-col items-center">
              <div
                title={step.label}
                className={`
                  flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300
                  ${isDone ? 'border-emerald-500 bg-emerald-500 text-white' : ''}
                  ${isActive ? 'border-emerald-500 bg-white text-emerald-600 shadow-md shadow-emerald-100 animate-pulse' : ''}
                  ${!isDone && !isActive ? 'border-slate-200 bg-white text-slate-400' : ''}
                `}
              >
                {isDone ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <span className="w-3.5 h-3.5 [&>svg]:w-3.5 [&>svg]:h-3.5">{step.icon}</span>
                )}
              </div>
              {/* Tooltip */}
              <div className="pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-0.5 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {step.label}
              </div>
            </div>

            {/* Connector line between pills */}
            {index < steps.length - 1 && (
              <div
                className={`
                  h-0.5 w-6 shrink-0 transition-colors duration-500
                  ${isDone ? 'bg-emerald-400' : 'bg-slate-200'}
                `}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
